import React from 'react';
import { Navigate } from 'react-router-dom';
import { AdminUser, ROLE_PERMISSIONS } from '../types';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  allowedRoles?: ('admin' | 'operator')[];
  fallbackPath?: string;
}

export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  requiredPermission,
  allowedRoles,
  fallbackPath = '/admin/dashboard',
}) => {
  // ログインユーザー情報を取得
  const adminUser: AdminUser | null = (() => {
    try {
      const userData = localStorage.getItem('adminUser');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  })();

  // ログインしていない場合はログインページへ
  if (!adminUser) {
    return <Navigate to="/login" replace />;
  }

  // 役割チェック
  if (allowedRoles && !allowedRoles.includes(adminUser.role)) {
    // 権限がない場合はフォールバックパスへリダイレクト
    const to = fallbackPath;
    return <Navigate to={to} replace />;
  }

  // 権限チェック
  if (requiredPermission) {
    const userPermissions = adminUser.permissions || ROLE_PERMISSIONS[adminUser.role] || [];
    if (!userPermissions.includes(requiredPermission as any)) {
      const to = fallbackPath;
      return <Navigate to={to} replace />;
    }
  }

  return <>{children}</>;
};

// 権限チェック用のHook
export const usePermissions = () => {
  const adminUser: AdminUser | null = (() => {
    try {
      const userData = localStorage.getItem('adminUser');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  })();

  const hasPermission = (permission: string): boolean => {
    if (!adminUser) return false;
    const userPermissions = adminUser.permissions || ROLE_PERMISSIONS[adminUser.role] || [];
    return userPermissions.includes(permission as any);
  };

  const hasRole = (role: 'admin' | 'operator'): boolean => {
    return adminUser?.role === role;
  };

  const isAdmin = (): boolean => {
    return adminUser?.role === 'admin';
  };

  const isOperator = (): boolean => {
    return adminUser?.role === 'operator';
  };

  return {
    adminUser,
    hasPermission,
    hasRole,
    isAdmin,
    isOperator,
  };
}; 